import useClassNames from '@medflex/hooks/useClassNames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, isValidElement } from 'react';
import { IconCloseSolid } from './Icons';
import OnlineStatus from './OnlineStatus';

const Avatar = ({
  innerRef,
  src,
  resizeDimension,
  alt,
  size,
  initials,
  isBlock = false,
  isOnline,
  deactivated,
  className,
  children,
  ...props
}) => {
  const [hasImage, setHasImage] = useState(!!src);
  const { t } = useTranslation('global');

  useEffect(() => {
    if (src) {
      setHasImage(true);
      return;
    }
    setHasImage(false);
  }, [src]);

  const cn = useClassNames(
    () => [
      'relative',
      !isBlock && 'inline-block',
      isBlock && 'block',
      'overflow-hidden',
      size === '5' && 'w-5',
      size === '5' && 'h-5',
      size === '6' && 'w-6',
      size === '6' && 'h-6',
      size === '8' && 'w-8',
      size === '8' && 'h-8',
      size === '10' && 'w-10',
      size === '10' && 'h-10',
      (!size || size === '12') && 'w-12',
      (!size || size === '12') && 'h-12',
      size === '16' && 'w-16',
      size === '16' && 'h-16',
      size === '20' && 'w-20',
      size === '20' && 'h-20',
      size === '24' && 'w-24',
      size === '24' && 'h-24',
      size === '32' && 'w-32',
      size === '32' && 'h-32',
      'select-none',
      'flex-shrink-0',
      className,
    ],
    [className, hasImage, size],
  );
  return (
    <div data-component="Avatar" className={cn} ref={innerRef} {...props}>
      <div
        className={`flex h-full w-full rounded-full overflow-hidden border-blue-500 bg-blue-100 items-center justify-center${
          !hasImage ? ' border' : ''
        }`}
      >
        {!hasImage && initials && (
          <svg
            viewBox="-16 -16 32 32"
            preserveAspectRatio="xMidYMid slice"
            className="block absolute left-0 top-0 w-full h-full pointer-events-none"
          >
            <text
              dy=".325em"
              textAnchor="middle"
              className="font-normal text-slate-500 pointer-events-auto"
              fill="currentColor"
            >
              {initials.slice(0, 2).toUpperCase()}
            </text>
          </svg>
        )}
        {hasImage &&
          src &&
          (isValidElement(src) ? (
            src
          ) : (
            <img
              src={`${src}${resizeDimension ? `?resize=${resizeDimension}` : ''}`}
              alt={alt}
              className="block w-full h-full object-contain"
              onError={() => {
                setHasImage(false);
              }}
            />
          ))}
        {children}
      </div>
      {isOnline && <OnlineStatus className="absolute bottom-0 right-0 ring-1 ring-white" />}
      {deactivated && (
        <IconCloseSolid
          title={t('inactive')}
          width="14px"
          height="14px"
          shape="circle"
          className="absolute bottom-0 right-0 text-slate-600 bg-white rounded-full"
        />
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  alt: PropTypes.string,
  initials: PropTypes.string,
  size: PropTypes.oneOf(['none', '5', '6', '8', '10', '12', '16', '20', '24', '32']),
  resizeDimension: PropTypes.string,
  isBlock: PropTypes.bool,
  isOnline: PropTypes.bool,
  deactivated: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  innerRef: PropTypes.shape({ current: PropTypes.any }),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  children: PropTypes.node,
};

Avatar.defaultProps = {
  resizeDimension: '200x',
};

export default Avatar;
